import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Trash2, Plus, Save, Map as MapIcon } from "lucide-react";

/**
 * Admin: pick & order the courses that appear as gamified nodes on the landing
 * page roadmap. Length is fully dynamic. Order is preserved.
 */
export default function AdminLandingJourneyPage() {
  const [available, setAvailable] = useState([]);
  const [pickedIds, setPickedIds] = useState([]);
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [pendingAdd, setPendingAdd] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [cur, courses] = await Promise.all([
        api.get("/landing/journey"),
        api.get("/courses", { params: { status: "published" } }),
      ]);
      setAvailable(courses.data || []);
      setTitle(cur.data?.title || "");
      setSubtitle(cur.data?.subtitle || "");
      setPickedIds((cur.data?.nodes || []).map((n) => n.id));
    } catch (e) {
      toast.error("Failed to load courses");
      setAvailable([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const byId = useMemo(() => {
    const m = {};
    available.forEach((c) => {
      m[c.id] = c;
    });
    return m;
  }, [available]);

  const pickedCourses = pickedIds.map((id) => byId[id]).filter(Boolean);
  const remaining = available.filter((c) => !pickedIds.includes(c.id));

  const add = () => {
    if (!pendingAdd) return;
    if (pickedIds.includes(pendingAdd)) {
      toast.warning("Already in the roadmap");
      return;
    }
    setPickedIds([...pickedIds, pendingAdd]);
    setPendingAdd("");
  };
  const move = (i, dir) => {
    const next = [...pickedIds];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setPickedIds(next);
  };
  const remove = (i) => setPickedIds(pickedIds.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/landing/journey", {
        course_ids: pickedIds,
        title,
        subtitle,
      });
      toast.success("Landing roadmap updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-landing-journey-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">LANDING PAGE</div>
          <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1 inline-flex items-center gap-3">
            <MapIcon className="w-7 h-7 text-kb-primary" strokeWidth={1.5} />
            Journey <em className="italic text-kb-primary">level map</em>
          </h1>
          <p className="text-sm text-kb-ink-muted mt-2 max-w-2xl">
            Pick which LMS courses appear as roadmap nodes on the landing page, and order them. The last node is the
            &ldquo;boss level&rdquo; (final stop). Premium courses render as locked checkpoints.
          </p>
        </div>
        <button onClick={save} disabled={saving} className="kb-btn-primary text-sm" data-testid="journey-save-btn">
          <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save roadmap"}
        </button>
      </div>

      {/* Headline overrides */}
      <div className="mt-6 kb-card p-5 space-y-3">
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-primary">SECTION COPY (optional)</div>
        <input
          className="kb-input text-sm w-full"
          placeholder="Headline override (leave blank to use default)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          data-testid="journey-title-input"
        />
        <textarea
          rows={2}
          className="kb-input text-sm w-full resize-none"
          placeholder="Subtitle override"
          value={subtitle}
          onChange={(e) => setSubtitle(e.target.value)}
          data-testid="journey-subtitle-input"
        />
      </div>

      {/* Add picker */}
      <div className="mt-6 kb-card p-5">
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-primary mb-2">ADD A LEVEL</div>
        <div className="flex gap-2">
          <select
            className="kb-input text-sm flex-1"
            value={pendingAdd}
            onChange={(e) => setPendingAdd(e.target.value)}
            data-testid="journey-add-select"
          >
            <option value="">— Pick a course to add —</option>
            {remaining.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title} {c.level ? `· ${c.level}` : ""} {((c.price_npr || 0) > 0) ? "· Premium" : ""}
              </option>
            ))}
          </select>
          <button
            onClick={add}
            disabled={!pendingAdd}
            className="kb-btn-primary text-sm whitespace-nowrap"
            data-testid="journey-add-btn"
          >
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        </div>
        {available.length === 0 && !loading && (
          <div className="mt-3 text-xs text-rose-600">
            No published LMS courses found. Create or publish courses first (Admin → Course Review).
          </div>
        )}
      </div>

      {/* Ordered list of picks */}
      <div className="mt-6">
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-2">
          ROADMAP NODES ({pickedCourses.length})
        </div>
        {loading ? (
          <div className="kb-mono text-xs text-kb-ink-muted">Loading…</div>
        ) : pickedCourses.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-slate-300 rounded-sm bg-white text-sm text-kb-ink-muted">
            No nodes yet. Pick a course above to add the first level. The landing page section will hide itself until
            at least one node is added.
          </div>
        ) : (
          <ol className="space-y-2">
            {pickedCourses.map((c, i) => {
              const isBoss = i === pickedCourses.length - 1;
              const isPremium = (c.price_npr || 0) > 0;
              return (
                <li
                  key={c.id}
                  className="kb-card p-4 flex items-center gap-3"
                  data-testid={`journey-row-${i}`}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-white kb-mono text-sm ${
                      isBoss ? "bg-amber-500" : "bg-kb-primary"
                    }`}
                  >
                    {isBoss ? "★" : String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-base text-kb-ink leading-tight">{c.title}</div>
                    <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-0.5">
                      {isBoss ? "BOSS · " : `LVL ${String(i + 1).padStart(2, "0")} · `}
                      {c.level || "ALL"} {isPremium ? "· PREMIUM (locked icon)" : "· FREE"}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => move(i, -1)}
                      disabled={i === 0}
                      className="p-1.5 text-kb-ink-muted hover:text-kb-primary disabled:opacity-30"
                      data-testid={`journey-row-${i}-up`}
                      title="Move up"
                    >
                      <ArrowUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => move(i, 1)}
                      disabled={i === pickedCourses.length - 1}
                      className="p-1.5 text-kb-ink-muted hover:text-kb-primary disabled:opacity-30"
                      data-testid={`journey-row-${i}-down`}
                      title="Move down"
                    >
                      <ArrowDown className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => remove(i)}
                      className="p-1.5 text-rose-600 hover:text-rose-700"
                      data-testid={`journey-row-${i}-remove`}
                      title="Remove"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
}
